import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, Bell, AlertTriangle, FolderKanban, ChevronRight, Zap, Gavel, Radio, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtRelative, templatesMeta } from "../lib/format";
import { ScoreBadge } from "../components/ScoreBadge";
import ResultModal from "../components/ResultModal";
import TenderModal from "../components/TenderModal";
import { useAuth } from "../lib/auth";

const PROJECTS_PAGE_SIZE = 5;
const ACTION_ITEMS_PREVIEW_COUNT = 2;

const StatCard = ({ icon: Icon, label, value, color, testId, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="text-left bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#0F1F3D] hover:-translate-y-0.5 transition-all"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="type-eyebrow text-[#64748B]">{label}</div>
        <div className="font-mono-tab text-[26px] leading-[32px] font-semibold text-[#0F1F3D] mt-2" style={{ letterSpacing: "-0.02em" }}>{value}</div>
        {/* <div className="type-caption text-[#94A3B8] mt-2">Click to filter →</div> */}
      </div>
      <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ background: color + "15", color }}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </button>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [data, setData] = useState(null);
  const [orgContextFilled, setOrgContextFilled] = useState(true);
  const [projectMap, setProjectMap] = useState({});
  // P2 — open the SAME result modal (as news) with the AI action highlighted.
  const [activeItem, setActiveItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTender, setActiveTender] = useState(null);
  const [tenderModalOpen, setTenderModalOpen] = useState(false);
  const [bookmarkIds, setBookmarkIds] = useState(new Set());
  const [projectPage, setProjectPage] = useState(0);
  const [recommendationTab, setRecommendationTab] = useState("news");

  const nav = useNavigate();


  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/org/context").then((r) => setOrgContextFilled(!!r.data?.context_filled)).catch(() => {});
    api.get("/projects").then((r) => {
      const m = {};
      (r.data?.projects || []).forEach((p) => { m[p.id] = p; });
      setProjectMap(m);
    }).catch(() => {});
    api.get("/bookmarks/ids").then((r) => setBookmarkIds(new Set(r.data?.ids || []))).catch(() => {});
  }, []);

  const openNewsAction = useCallback((it) => {
    setActiveItem(it);
    setModalOpen(true);
    if (!it.id) return;
    api.post(`/results/${it.id}/read`).catch(() => {});
  }, []);

  const toggleBookmark = useCallback(async (item) => {
    const isOn = bookmarkIds.has(item.id);
    setBookmarkIds((prev) => {
      const next = new Set(prev);
      if (isOn) next.delete(item.id); else next.add(item.id);
      return next;
    });
    try {
      if (isOn) { await api.delete(`/bookmarks/${item.id}`); toast.success("Bookmark removed"); }
      else { await api.post("/bookmarks", { result_id: item.id, project_id: item.project_id }); toast.success("Saved to bookmarks"); }
    } catch (err) {
      setBookmarkIds((prev) => {
        const next = new Set(prev);
        if (isOn) next.add(item.id); else next.delete(item.id);
        return next;
      });
      toast.error(err?.response?.data?.detail || "Bookmark failed");
    }
  }, [bookmarkIds]);

  const onFeedback = useCallback(async (item, val) => {
    try { await api.post(`/results/${item.id}/feedback`, { feedback: val }); toast.success("Feedback recorded"); }
    catch { toast.error("Feedback failed"); }
  }, []);

  const onActionStatusChange = useCallback((item, data) => {
    const patch = {
      action_status: data.action_status,
      action_closed_note: data.action_closed_note ?? null,
      action_closed_at: data.action_closed_at ?? null,
    };
    const wasOpen = (item.action_status || "open") !== "closed";
    const isOpen = data.action_status !== "closed";
    const countDelta = Number(isOpen) - Number(wasOpen);
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stats: {
          ...prev.stats,
          action_required: Math.max(0, prev.stats.action_required + countDelta),
        },
        news_action_items: (prev.news_action_items || []).filter((a) =>
          a.id !== item.id || data.action_status !== "closed"
        ).map((a) => a.id === item.id ? { ...a, ...patch } : a),
        action_items: (prev.action_items || []).map((a) =>
          a.id === item.id ? { ...a, ...patch } : a
        ),
      };
    });
    setActiveItem((prev) => (prev && prev.id === item.id ? { ...prev, ...patch } : prev));
  }, []);

  const openTenderAction = useCallback((tender) => {
    setActiveTender(tender);
    setTenderModalOpen(true);
  }, []);

  const onTenderActionStatusChange = useCallback((tender, result) => {
    const patch = {
      action_status: result.action_status,
      action_closed_note: result.action_closed_note ?? null,
      action_closed_at: result.action_closed_at ?? null,
    };
    const wasOpen = (tender.action_status || "open") !== "closed";
    const isOpen = result.action_status !== "closed";
    const countDelta = Number(isOpen) - Number(wasOpen);
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stats: {
          ...prev.stats,
          action_required: Math.max(0, prev.stats.action_required + countDelta),
        },
        tender_action_items: (prev.tender_action_items || []).filter((item) =>
          item.id !== tender.id || result.action_status !== "closed"
        ).map((item) => item.id === tender.id ? { ...item, ...patch } : item),
        action_items: (prev.action_items || []).map((item) =>
          item.id === tender.id ? { ...item, ...patch } : item
        ),
      };
    });
    setActiveTender((prev) => (prev && prev.id === tender.id ? { ...prev, ...patch } : prev));
  }, []);

  if (!data) {
    return <div className="p-10 text-[#64748B]" data-testid="dashboard-loading">Loading dashboard…</div>;
  }

  const { stats, project_summaries } = data;
  const recommendationItems = recommendationTab === "news"
    ? (data.news_action_items || (data.action_items || []).filter((item) => item.kind === "news"))
    : (data.tender_action_items || (data.action_items || []).filter((item) => item.kind === "tender"));

  const totalProjectPages = Math.max(1, Math.ceil(project_summaries.length / PROJECTS_PAGE_SIZE));
  const currentProjectPage = Math.min(projectPage, totalProjectPages - 1);
  const pagedProjectSummaries = project_summaries.slice(
    currentProjectPage * PROJECTS_PAGE_SIZE,
    currentProjectPage * PROJECTS_PAGE_SIZE + PROJECTS_PAGE_SIZE
  );

  const visibleItems = recommendationItems.slice(0, ACTION_ITEMS_PREVIEW_COUNT);

  return (
    <div className="p-8 lg:p-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="type-eyebrow text-[#64748B]">Today&apos;s intelligence</div>
          <h1 className="type-h1 text-[#0F1F3D] mt-3">Dashboard</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/app/feed" data-testid="dashboard-open-feed" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#0F1F3D] text-[#0F1F3D] hover:bg-[#F1F5F9] text-sm font-medium">
            Open feed <ChevronRight className="h-4 w-4" />
          </Link>
          {!isOrgMember && (
            <Link to="/app/projects/new" data-testid="dashboard-new-project" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F] text-sm font-medium">
              New project
            </Link>
          )}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-active-projects" icon={FolderKanban} label="Active Projects" value={stats.active_projects} color="#0F1F3D"
          onClick={() => nav("/app/projects")} />
        <StatCard testId="stat-total-results" icon={Activity} label="Total Results" value={stats.total_results} color="#10B981"
          onClick={() => nav("/app/feed")} />
        <StatCard testId="stat-high-alerts" icon={Bell} label="High Alerts" value={stats.high_alerts} color="#EF4444"
          onClick={() => nav("/app/feed?priority=high")} />
        <StatCard testId="stat-action-required" icon={AlertTriangle} label="Strategic Recommendations" value={stats.action_required} color="#F59E0B"
          onClick={() => nav("/app/feed?action_required=true&action_status=open")} />
      </div>

      {!orgContextFilled && (
        <Link
          to="/app/organisation"
          data-testid="dashboard-actions-locked"
          className="mt-6 block border border-dashed border-[#CBD5E1] rounded-lg px-5 py-4 bg-[#F8FAFC] hover:border-[#2563EB] transition-colors"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[#EFF6FF] text-[#2563EB]">
                <Zap className="h-4 w-4" />
              </div>
              <div>
                <div className="type-eyebrow text-[#64748B]">Strategic Recommendations · Locked</div>
                <div className="text-sm text-[#0F1F3D] font-semibold mt-0.5">
                  Fill in About Organisation to unlock strategic recommendations
                </div>
                <div className="type-caption text-[#94A3B8] mt-0.5">
                  Tell us what your business does and we will suggest concrete, tied-to-you next steps.
                </div>
              </div>
            </div>
            <div className="type-caption font-semibold flex items-center gap-1 text-[#2563EB]">
              Set up now <ChevronRight className="h-3 w-3" />
            </div>
          </div>
        </Link>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Projects table */}
        <div className="lg:col-span-8 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-center justify-between">
            <div>
              <div className="type-eyebrow text-[#64748B]">Projects</div>
              <div className="type-h3 text-[#0F1F3D] mt-1">Project summary</div>
            </div>
            <Link to="/app/projects" data-testid="dashboard-projects-link" className="type-body-sm text-[#0F1F3D] hover:underline font-medium">View all</Link>
          </div>

          <div>
            <table className="w-full">
              <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B] sticky top-0 z-10">
                <tr>
                  <th className="text-left px-6 py-3">Project</th>
                  <th className="text-left px-6 py-3">Templates</th>
                  <th className="text-right px-6 py-3">Results</th>
                  <th className="text-right px-6 py-3">High</th>
                  <th className="text-right px-6 py-3">Last run</th>
                </tr>
              </thead>

              <tbody>
                {project_summaries.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-[#94A3B8] type-body"
                    >
                      No projects yet — create one to start receiving intelligence.
                    </td>
                  </tr>
                )}

                {pagedProjectSummaries.map((p) => {
                  const tms = templatesMeta(p.template_ids || []);

                  return (
                    <tr
                      key={p.id}
                      onClick={() => nav(`/app/feed?project_id=${p.id}`)}
                      data-testid={`project-row-${p.id}`}
                      className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="type-h4 text-[#0F1F3D]">{p.name}</div>
                          {p.new_results_count > 0 && (
                            <span
                              data-testid={`project-new-badge-${p.id}`}
                              title={`${p.new_results_count} new in the last 24h`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#047857] text-[11px] font-bold leading-none"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                              {p.new_results_count} new
                            </span>
                          )}
                        </div>

                        {(p.sectors || []).length > 0 && (
                          <div className="type-caption text-[#64748B] mt-1">
                            {(p.sectors || []).join(" · ")}
                          </div>
                        )}

                        {p.is_active === true ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#047857] text-[11px] font-bold leading-none">Active</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-600 text-[11px] font-bold leading-none">
                            Inactive
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {tms.map((tm) => (
                            <span
                              key={tm.id || tm.label}
                              className="px-2 py-1 rounded-md type-eyebrow"
                              style={{
                                background: tm.color + "15",
                                color: tm.color,
                              }}
                            >
                              {tm.label}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-right font-mono-tab text-sm text-[#0F1F3D]">
                        {p.results_count}
                      </td>

                      <td className="px-6 py-4 text-right font-mono-tab text-sm font-bold text-[#B91C1C]">
                        {p.high_alerts}
                      </td>

                      <td className="px-6 py-4 text-right type-caption text-[#64748B]">
                        {fmtRelative(p.last_run_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalProjectPages > 1 && (
            <div
              className="mt-auto px-6 py-3 border-t border-[#E2E8F0] flex items-center gap-3 justify-start"
              data-testid="dashboard-projects-pagination"
            >
              <span className="type-caption text-[#64748B]">
                Page {currentProjectPage + 1} of {totalProjectPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setProjectPage((pg) => Math.max(0, pg - 1))}
                  disabled={currentProjectPage === 0}
                  data-testid="dashboard-projects-prev"
                  className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setProjectPage((pg) => Math.min(totalProjectPages - 1, pg + 1))}
                  disabled={currentProjectPage >= totalProjectPages - 1}
                  data-testid="dashboard-projects-next"
                  className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recommended actions (P5) — news + tenders, AI-decided by org context */}
        <div
          className="lg:col-span-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden flex flex-col"
          data-testid="action-required-list"
        >
          <div className="px-6 py-5 border-b border-[#E2E8F0]">
            <div className="space-y-3">
              <div className="type-eyebrow text-[#64748B]">Strategic Recommendations</div>
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex shrink-0 rounded-md border border-[#E2E8F0] p-1" role="tablist" aria-label="Recommendation type">
                  {["news", "tenders"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={recommendationTab === tab}
                      data-testid={`dashboard-recommendations-tab-${tab}`}
                      onClick={() => setRecommendationTab(tab)}
                      className={`px-3 py-1.5 rounded text-sm font-medium capitalize transition-colors ${recommendationTab === tab ? "bg-[#0F1F3D] text-white" : "text-[#475569] hover:bg-[#F1F5F9]"}`}
                    >
                      {tab === "news" ? "News" : "Tenders"}
                    </button>
                  ))}
                </div>
                <Link
                  to={recommendationTab === "news"
                    ? "/app/feed?action_required=true&action_status=open"
                    : "/app/tenders"}
                  data-testid="dashboard-actions-view-all"
                  className="type-body-sm text-[#0F1F3D] hover:underline font-medium shrink-0"
                >
                  View all
                </Link>
              </div>
            </div>
          </div>

          <div className="flex-1">
            {recommendationItems.length > 0 ? (
              <ul className="divide-y divide-[#E2E8F0]">
                {visibleItems.map((it, i) => {
                  const isTender = it.kind === "tender";
                  return (
                    <li key={it.id || i} data-testid={`action-item-${it.id || i}`} className="px-6 py-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${isTender ? "bg-[#FFFBEB] text-[#B45309]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}
                          data-testid={`action-kind-${it.id || i}`}
                        >
                          {isTender ? <Gavel className="h-3 w-3" /> : <Radio className="h-3 w-3" />}
                          {isTender ? "Tender" : "News"}
                        </span>
                        <span
                          data-testid={`action-status-${it.id || i}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#ECFDF5] text-[#047857]"
                        >
                          Open
                        </span>
                        {typeof it.relevance_score === "number" && <ScoreBadge score={it.relevance_score} />}
                      </div>
                      {isTender ? (
                        <div className="type-h4 text-[#0F1F3D] leading-snug">{it.title}</div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openNewsAction(it)}
                          data-testid={`action-open-${it.id || i}`}
                          className="text-left type-h4 text-[#0F1F3D] leading-snug hover:underline"
                        >
                          {it.title}
                        </button>
                      )}
                      {it.recommended_action && (
                        <div className="mt-1.5 flex items-start gap-1.5 text-sm text-[#0F1F3D]">
                          <AlertTriangle className="h-3.5 w-3.5 text-[#F59E0B] mt-0.5 shrink-0" />
                          <span>{it.recommended_action}</span>
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="type-caption text-[#94A3B8] truncate">{it.source || (isTender ? "Tender portal" : "")}</span>
                        {isTender ? (
                          <button
                            type="button"
                            onClick={() => openTenderAction(it)}
                            data-testid={`action-view-tender-${it.id || i}`}
                            className="type-caption font-semibold text-[#0F1F3D] hover:underline shrink-0"
                          >
                            View tender →
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openNewsAction(it)}
                            data-testid={`action-view-${it.id || i}`}
                            className="type-caption font-semibold text-[#0F1F3D] hover:underline shrink-0"
                          >
                            View action →
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : !orgContextFilled ? (
              <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center px-6" data-testid="dashboard-actions-fill-org">
                <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[#F1F5F9] text-[#0F1F3D]">
                  <Zap className="h-5 w-5" />
                </div>
                <p className="mt-3 type-body text-[#64748B] max-w-xs">
                  Fill in <Link to="/app/organisation" className="text-[#0F1F3D] font-semibold hover:underline">About Organisation</Link> to get tailored strategic recommendations from your news &amp; tenders.
                </p>
              </div>
            ) : (
              <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center px-6" data-testid="actions-empty">
                <p className="type-body text-[#64748B] max-w-xs">
                  No {recommendationTab === "news" ? "news" : "tender"} recommendations to review right now.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ResultModal
        item={activeItem}
        project={activeItem ? projectMap[activeItem.project_id] : null}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setActiveItem(null); }}
        isBookmarked={activeItem ? bookmarkIds.has(activeItem.id) : false}
        onToggleBookmark={toggleBookmark}
        onFeedback={onFeedback}
        onActionStatusChange={onActionStatusChange}
        highlightAction
      />
      <TenderModal
        tender={activeTender}
        projectName={activeTender ? projectMap[activeTender.project_id]?.name : ""}
        open={tenderModalOpen}
        onClose={() => { setTenderModalOpen(false); setActiveTender(null); }}
        isBookmarked={activeTender ? bookmarkIds.has(activeTender.id) : false}
        onToggleBookmark={toggleBookmark}
        onActionStatusChange={onTenderActionStatusChange}
      />
    </div>
  );
}